import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { bidsService } from '../../../utils/bidsService';
import Icon from '../../../components/AppIcon';

const BiddingPreview = ({ callData }) => {
  const { isAuthenticated } = useAuth();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [realTimeStats, setRealTimeStats] = useState({
    activeBids: 0,
    highestBid: 0,
    timeRemaining: '23:45:12'
  });

  useEffect(() => {
    if (callData?.id) {
      loadCallBids();
    } else {
      setBids([]);
      setRealTimeStats({
        activeBids: 0,
        highestBid: 0,
        timeRemaining: '24:00:00'
      });
    }
  }, [callData?.id]);

  const loadCallBids = async () => {
    if (!callData?.id) return;

    try {
      setLoading(true);
      
      const bidsData = await bidsService?.getCallBids(callData?.id);
      const activeBids = bidsData?.filter(bid => bid?.status === 'active') || [];
      
      setBids(bidsData || []);
      setRealTimeStats({
        activeBids: activeBids?.length,
        highestBid: activeBids?.reduce((max, bid) => Math.max(max, bid?.bid_amount || 0), 0),
        timeRemaining: calculateTimeRemaining(callData?.expires_at)
      });
      
    } catch (error) {
      console.error('Error loading bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeRemaining = (expiresAt) => {
    if (!expiresAt) return '24:00:00';
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry?.getTime() - now?.getTime();
    
    if (diff <= 0) return '00:00:00';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  useEffect(() => {
    if (callData?.expires_at) {
      const timer = setInterval(() => {
        setRealTimeStats(prev => ({
          ...prev,
          timeRemaining: calculateTimeRemaining(callData?.expires_at)
        }));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [callData?.expires_at]);

  if (!callData) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="Target" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Active Call</h3>
          <p className="text-muted-foreground">Submit a call to see the bidding preview</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Target" size={20} className="text-success" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Live Bidding</h2>
            <p className="text-sm text-muted-foreground">Real-time auction activity</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Call Summary */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium text-foreground">
              {callData?.insurance_type?.charAt(0)?.toUpperCase() + callData?.insurance_type?.slice(1)} Insurance
            </span>
            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
              {callData?.call_quality || 'Standard'}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">ZIP Code:</span>
              <span className="ml-2 text-foreground font-medium">{callData?.customer_zip_code}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Age:</span>
              <span className="ml-2 text-foreground font-medium">{callData?.customer_age}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Premium:</span>
              <span className="ml-2 text-foreground font-medium">${callData?.estimated_premium}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Reserve:</span>
              <span className="ml-2 text-foreground font-medium">${callData?.reserve_price}</span>
            </div>
          </div>
        </div>

        {/* Auction Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{realTimeStats?.activeBids}</div>
            <div className="text-xs text-muted-foreground">Active Bids</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              ${realTimeStats?.highestBid?.toFixed(2) || '0.00'}
            </div>
            <div className="text-xs text-muted-foreground">Highest Bid</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">{realTimeStats?.timeRemaining}</div>
            <div className="text-xs text-muted-foreground">Time Left</div>
          </div>
        </div>

        {/* Bidding Activity */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Recent Bids</h3>
            {loading && (
              <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
            )}
          </div>

          {bids?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="Clock" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Waiting for first bid...</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {bids?.slice(0, 10)?.map((bid, index) => (
                <div
                  key={bid?.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 && bid?.status === 'active' ?'bg-success/10 border border-success/20' :'bg-muted/20'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      index === 0 && bid?.status === 'active' ?'bg-success text-success-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      #{index + 1}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {bid?.buyer?.company_name || bid?.buyer?.full_name || 'Anonymous Buyer'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(bid?.created_at)?.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`font-bold ${
                      index === 0 && bid?.status === 'active' ?'text-success text-lg' :'text-foreground'
                    }`}>
                      ${bid?.bid_amount?.toFixed(2)}
                    </div>
                    {bid?.auto_generated && (
                      <div className="text-xs text-muted-foreground">Auto</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Estimated Value */}
        {realTimeStats?.highestBid > 0 && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Projected Final Value</span>
            </div>
            <div className="text-2xl font-bold text-success">
              ${(realTimeStats?.highestBid * 1.15)?.toFixed(2)}
            </div>
            <div className="text-xs text-success/80">
              Based on current bidding activity (+15% estimated)
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BiddingPreview;